import { useState, useRef, useCallback, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { fetchLinkMappings, processMessageContent, LinkMapping } from "./utils/linkConverter";

export interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: string | React.ReactNode;
  dont_show?: boolean;
  isComponent?: boolean;
  streaming?: boolean;
}

interface UseChatOptions {
  body: Record<string, any>;
  onStreamStart?: () => void;
  onStreamEnd?: () => void;
}

interface Contact {
  id: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  properties?: {
    createdate: string;
    email: string;
    firstname: string;
    hs_object_id: string;
    lastmodifieddate: string;
    lastname: string;
    machine_serial_number: string;
  };
}

interface UseChatReturn {
  messages: Message[];
  input: string;
  handleInputChange: (text: string) => void;
  handleSubmit: (message: string, dont_show?: boolean) => Promise<void>;
  isLoading: boolean;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  currentMessage: string;
  isStreaming: boolean;
  isMachineSerialVerified: boolean;
  currentContact: Contact | null;
  isSessionExpired: boolean;
  setWaitingForReview: (waiting: boolean) => void;
  waitingForReview: boolean;
  sessionId: string;
  handleReviewSubmit: (wasHelpful: boolean) => Promise<void>;
}

// const API_BASE_URL = "http://localhost:3022";
const API_BASE_URL = "https://lafly.latteartfactory.com/admin";


const SESSION_TIMEOUT = 60 * 60 * 1000; // 30 minutes in milliseconds

// Utility function to get session data
function getSessionData(): {
  sessionId: string;
  timestamp: number;
  email?: string;
  isMachineSerialVerified: boolean;
} {
  return JSON.parse(localStorage.getItem("chatSession_v2") || "{}");
}

// Utility function to set session data
function setSessionData(sessionData: {
  sessionId: string;
  timestamp: number;
  email?: string;
  isMachineSerialVerified: boolean;
}) {
  localStorage.setItem("chatSession_v2", JSON.stringify(sessionData));
}

// Utility function to get email sessions
function getEmailSessions(): Record<
  string,
  { sessionId: string; isMachineSerialVerified: boolean; timestamp: number }
> {
  return JSON.parse(localStorage.getItem("emailSessions_v2") || "{}");
}

// Utility function to set email sessions
function setEmailSessions(
  emailSessions: Record<
    string,
    { sessionId: string; isMachineSerialVerified: boolean; timestamp: number }
  >
) {
  localStorage.setItem("emailSessions_v2", JSON.stringify(emailSessions));
}

// Utility function to get URL parameters
function getUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    email: urlParams.get('email'),
  };
}

export function useChat(options: UseChatOptions): UseChatReturn {
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isMachineSerialVerified, setIsMachineSerialVerified] = useState(false);
  const isMachineSerialVerifiedRef = useRef(false);
  const isAnswerToQuestionRef = useRef(false);
  const sessionIdRef = useRef(uuidv4());
  const currentContactRef = useRef<Contact | null>(null);
  const [waitingForReview, setWaitingForReview] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [linkMappings, setLinkMappings] = useState<LinkMapping[]>([]);

  // Add a ref to track if we're restoring history
  const isRestoringHistoryRef = useRef(false);

  // Add AbortController ref
  const currentStreamController = useRef<AbortController | null>(null);

  // Add ref to track if we've processed URL email
  const hasProcessedUrlEmailRef = useRef(false);

  const handleInputChange = (text: string) => {
    setInput(text);
  };

  // Function to load link mappings
  const loadLinkMappings = useCallback(async () => {
    try {
      const mappings = await fetchLinkMappings(API_BASE_URL);
      setLinkMappings(mappings);
      console.log("🔗 Loaded link mappings:", mappings.length, "mappings");
    } catch (error) {
      console.error("Error loading link mappings:", error);
    }
  }, []);

  // Function to process message content for partner responses
  const processPartnerMessage = useCallback((content: string | React.ReactNode): string | React.ReactNode => {
    // Check if content contains link IDs (format: [Description](number))
    if (typeof content === 'string' && /\[([^\]]*)\]\(\d+\)/.test(content)) {
      console.log("🔗 Detected link IDs in partner response, converting to actual links");
      return processMessageContent(content, linkMappings);
    }
    return content;
  }, [linkMappings]);

  const handleSubmit = async (message: string, dont_show?: boolean) => {
    if (isRestoringHistoryRef.current) {
      console.log("🚫 Ignoring message while restoring history");
      return;
    }
    if (!message.trim()) return;

    // Reset review state at start of new message
    setWaitingForReview(false);

    options.onStreamStart?.();
    setInput("");
    setIsLoading(true);
    setIsStreaming(true);

    const userMessage: Message = {
      role: "user",
      content: message,
      dont_show: dont_show,
      id: new Date().getTime().toString(),
    };

    setAllMessages((prev) => [...prev, userMessage]);

    // Filter out system messages and component messages
    const messagesToSend = allMessages
      .filter((msg) => msg.role === "user" || msg.role === "assistant")
      .filter((msg) => !msg.isComponent)
      .map(({ role, content }) => ({ role, content }));

    try {
      // Create new AbortController for this stream
      currentStreamController.current = new AbortController();

      const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
            messages: [
              ...messagesToSend,
              { role: userMessage.role, content: userMessage.content },
            ],
            language: options.body.language,
            isMachineSerialVerified: isMachineSerialVerifiedRef.current,
            isAnswerToQuestion: isAnswerToQuestionRef.current,
            contactId: currentContactRef.current?.id,
            sessionId: sessionIdRef.current,
            urlEmail: getUrlParams().email, // Pass URL email to backend
          }),
        signal: currentStreamController.current.signal, // Add signal to request
      });

      if (!response.ok) throw new Error("Stream response not ok");
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No reader available");

      const decoder = new TextDecoder();
      let currentLoopMessages = new Map<string, Message>();
      let accumulatedContent = new Map<string, string>();
      let finalMessages = new Map();

      isAnswerToQuestionRef.current = false;

      console.log("🔄 Starting to process stream chunks");
      let chunkCounter = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        
        const messages = chunk.match(/data: [^\n]*/g) || [];

        for (const message of messages) {
          if (message.trim().startsWith("data: ")) {
            const data = message.slice(5).trim();
            if (data === "[DONE]") {
              console.log("Stream completed");
              continue;
            }

            try {
              const parsed = JSON.parse(data);
              const loopId = parsed.loopId;

              if (!loopId) {
                console.warn("Received message without loopId:", data);
                continue;
              }

              if (parsed.type === "function") {
                console.log("parsed", parsed);
                console.log("parsed.functionName", parsed.functionName);

                switch (parsed.functionName) {
                  // case "create_contact":
                  case "confirm_serial_number":
                  case "update_serial_number":
                    const previousValue = isMachineSerialVerifiedRef.current;
                    updateMachineSerialVerification(true);
                    console.log("🔐 Machine serial verification changed1:", {
                      from: previousValue,
                      to: isMachineSerialVerifiedRef.current,
                    });

                    console.log("🔐 Updating machine verification state:", {
                      before: {
                        currentSession: getSessionData(),
                        emailSessions: getEmailSessions(),
                      },
                    });

                    // Update both storages
                    const currentSession = getSessionData();
                    const emailSessions = getEmailSessions();

                    console.log("🔐 Updating machine verification state:", {
                      before: {
                        currentSession,
                        emailSessions,
                      },
                    });

                    if (currentSession.email) {
                      // Store verification state with email mapping
                      emailSessions[currentSession.email] = {
                        sessionId: currentSession.sessionId,
                        isMachineSerialVerified: true,
                        timestamp: currentSession.timestamp,
                      };
                      setEmailSessions(emailSessions);

                      console.log("📝 Updated emailSessions:", {
                        email: currentSession.email,
                        newState: emailSessions[currentSession.email],
                      });
                    }

                    // Also update current session
                    const updatedSession = {
                      ...currentSession,
                      isMachineSerialVerified: true,
                    };
                    setSessionData(updatedSession);

                    console.log("✅ Machine verification complete:", {
                      after: {
                        chatSession: updatedSession,
                        emailSessions: getEmailSessions(),
                        ref: isMachineSerialVerifiedRef.current,
                      },
                    });
                    break;
                  case "query_knowledge_base":
                  case "get_error_code":
                    console.log("🎯 Function call detected that should trigger review:", parsed.functionName);
                    isAnswerToQuestionRef.current = true;
                    break;
                  case "check_hubspot_email":
                  case "create_contact":
                    if (parsed.contact) {
                      console.log("👤 Contact found/created:", parsed.contact);
                      console.log("👤 Contact ID:", parsed.contact.id);
                      currentContactRef.current = parsed.contact;
                    }
                    break;
                }
                continue;
              }

              if (parsed.type === "content") {
                if (!accumulatedContent.has(loopId)) {
                  accumulatedContent.set(loopId, "");
                }

                if (!currentLoopMessages.has(loopId)) {
                  const newMessage = {
                    role: "assistant" as const,
                    content: "",
                    id: new Date().getTime().toString(),
                    streaming: true, // Flag to indicate this message is still streaming
                  };
                  currentLoopMessages.set(loopId, newMessage);
                  console.log(`📱 Adding new streaming UI message: id=${newMessage.id}, loopId=${loopId}`);
                  setAllMessages((prev) => [...prev, newMessage as Message]);
                }

                const currentLoopMessage = currentLoopMessages.get(loopId)!;
                const previousContent = accumulatedContent.get(loopId)!;
                const newContent = previousContent + parsed.content;
                
                accumulatedContent.set(loopId, newContent);
                
                // Process content for partner responses (convert link IDs to actual links)
                const processedContent = processPartnerMessage(newContent);
                currentLoopMessage.content = processedContent;

                // Store the final content for each message
                finalMessages.set(loopId, { ...currentLoopMessage, content: processedContent });

                setAllMessages((prev) => {
                  const messageExists = prev.some(msg => msg.id === currentLoopMessage.id);
                  
                  if (messageExists) {
                    console.log(`📱 Updating streaming UI message: id=${currentLoopMessage.id}, contentLength=${newContent.length}`);
                  }
                  
                  return prev.map((msg) =>
                    msg.id === currentLoopMessage.id
                      ? { ...msg, content: processedContent, streaming: true }
                      : msg
                  );
                });
              }
            } catch (e) {
              console.error("Error parsing message:", e);
            }
          }
        }
      }

      console.log("🏁 Stream processing complete");
      
      // Replace streaming messages with their final versions
      setAllMessages((prev) => {
        return prev.map((msg) => {
          // Convert Map entries to array before iteration
          const finalMsg = Array.from(finalMessages.values()).find(
            (finalMsg) => finalMsg.id === msg.id
          );
          
          if (finalMsg) {
            console.log(`📱 Replacing streaming message with final content: id=${msg.id}`);
            // Process final content for partner responses
            const processedFinalContent = processPartnerMessage(finalMsg.content);
            return { ...finalMsg, content: processedFinalContent, streaming: false }; // Mark as no longer streaming
          }
          return msg;
        });
      });
      
      console.log(`📱 Final UI state: ${finalMessages.size} messages displayed`);
      Array.from(finalMessages.entries()).forEach(([loopId, message]) => {
        console.log(`   - Message ${message.id}: final content length=${message.content?.toString().length}`);
      });
      
      setCurrentMessage("");
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("🛑 Stream was aborted");
      } else {
        console.error("Error in handleSubmit:", error);
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      options.onStreamEnd?.();
      console.log("currentContact", currentContactRef.current);
      console.log("isAnswerToQuestion", isAnswerToQuestionRef.current);

      // Show review buttons for all assistant responses when there's a contact
      console.log("🔍 Review button conditions:", {
        hasContact: !!currentContactRef.current?.id,
        contactId: currentContactRef.current?.id,
        isAnswerToQuestion: isAnswerToQuestionRef.current,
        willShowReview: !!currentContactRef.current?.id
      });
      
      if (currentContactRef.current?.id) {
        console.log("✅ Showing review buttons");
        setWaitingForReview(true);
        isAnswerToQuestionRef.current = false;
      } else {
        console.log("❌ Not showing review buttons - no contact found");
      }
    }
  };

  const handleReviewSubmit = async (wasHelpful: boolean) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          wasHelpful,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit review");
      }

      console.log("Review submitted:", wasHelpful);
      setWaitingForReview(false);
    } catch (error) {
      console.error("Error submitting review:", error);
    }
  };

  // Modify loadChatHistory
  const loadChatHistory = useCallback(
    async (sessionId: string, email: string) => {
      if (!email) return;

      try {
        isRestoringHistoryRef.current = true;

        // Get email-to-session mappings from localStorage
        const emailSessions = getEmailSessions();
        console.log("📝 Current email-session mappings:", {
          allMappings: emailSessions,
          totalSessions: Object.keys(emailSessions).length,
        });

        // Check if we have an existing active session for this email
        const existingSessionId = emailSessions[email];
        const existingSession = existingSessionId && getSessionData();
        const isSessionActive =
          existingSession &&
          Date.now() - existingSession.timestamp < SESSION_TIMEOUT;

        console.log("🔍 Session lookup result:", {
          email,
          existingSessionId,
          sessionAge: existingSession
            ? `${Math.round((Date.now() - existingSession.timestamp) / 1000)}s`
            : "N/A",
          isActive: isSessionActive,
        });

        // if (existingSessionId && existingSession && isSessionActive) {
        //   const emailSession = emailSessions[email];
        //   console.log("🔍 Found email session:", {
        //     email,
        //     emailSession,
        //     chatSession: existingSession,
        //   });

        //   sessionIdRef.current =
        //     typeof emailSession === "object"
        //       ? emailSession.sessionId
        //       : emailSession;

        //   // First check if machine was verified in previous session
        //   const wasVerified =
        //     (typeof emailSession === "object" &&
        //       emailSession.isMachineSerialVerified) ||
        //     existingSession.isMachineSerialVerified ||
        //     false;

        //   console.log("🔐 Checking previous verification state:", {
        //     emailSessionVerified:
        //       typeof emailSession === "object" &&
        //       emailSession.isMachineSerialVerified,
        //     chatSessionVerified: existingSession.isMachineSerialVerified,
        //     finalVerificationState: wasVerified,
        //   });

        //   // If verified, restore chat history first
        //   if (wasVerified) {
        //     // Fetch chat history from database
        //     const response = await fetch(
        //       `${API_BASE_URL}/api/review/${sessionIdRef.current}/chats`
        //     );
        //     if (!response.ok) throw new Error("Failed to fetch chat history");

        //     const { data } = await response.json();
        //     const messageCount = data?.length || 0;

        //     console.log("📝 Loaded chat history for verified machine:", {
        //       messageCount,
        //       sessionId: sessionIdRef.current,
        //     });

        //     // Only restore if we actually have messages
        //     if (data && messageCount > 0) {
        //       // Abort stream only if we have messages to restore
        //       if (currentStreamController.current) {
        //         console.log(
        //           "🛑 Aborting current stream - restoring verified chat history"
        //         );
        //         currentStreamController.current.abort();
        //         currentStreamController.current = null;
        //       }

        //       setAllMessages(data);
        //       setIsLoading(false);
        //       setIsStreaming(false);
        //       setCurrentMessage("");
        //       // Then update the verification state
        //       const previousVerifiedState = isMachineSerialVerifiedRef.current;
        //       updateMachineSerialVerification(wasVerified);
        //       console.log("🔐 Machine serial verification changed2:", {
        //         from: previousVerifiedState,
        //         to: isMachineSerialVerifiedRef.current,
        //       });
        //     } else {
        //       console.log("ℹ️ No chat history to restore for verified machine");
        //     }
        //   } else {
        //     console.log(
        //       "⚠️ Skipping chat history - machine not verified in previous session"
        //     );
        //   }

        //   // Update storage with current state
        //   setSessionData({
        //     sessionId: sessionIdRef.current,
        //     timestamp: Date.now(),
        //     email,
        //     isMachineSerialVerified: wasVerified,
        //   });
        // } else {
        console.log("🆕 Creating new email-session mapping:", {
          email,
          newSessionId: sessionId,
          replacingSessionId: existingSessionId,
        });
        emailSessions[email] = {
          sessionId,
          timestamp: Date.now(),
          isMachineSerialVerified: isMachineSerialVerifiedRef.current,
        };
        setEmailSessions(emailSessions);

        // Let's add debug logs
        console.log("Debug sessionId when storing:", {
          rawSessionId: sessionId,
          type: typeof sessionId,
          emailSessionObject: emailSessions[email],
        });

        setSessionData({
          sessionId,
          timestamp: Date.now(),
          email,
          isMachineSerialVerified: isMachineSerialVerifiedRef.current,
        });
        // }

        // Log final state
        console.log("📊 Updated email-session state:", {
          finalSessionId: sessionIdRef.current,
          email,
          allMappings: getEmailSessions(),
        });
      } catch (error) {
        console.error("Error in loadChatHistory:", error);
      } finally {
        isRestoringHistoryRef.current = false;
      }
    },
    []
  );

  // Flag to ensure we only load history once per session
  const hasLoadedHistoryRef = useRef(false);

  // Called once when component mounts to create initial session
  const initializeSession = useCallback(() => {
    const newSessionId = uuidv4();
    console.log("Initializing new session:", newSessionId);
    sessionIdRef.current = newSessionId;
    setSessionData({
      sessionId: newSessionId,
      timestamp: Date.now(),
      isMachineSerialVerified: false,
    });
    console.log("Current localStorage state:", {
      chatSession: getSessionData(),
      emailSessions: getEmailSessions(),
    });
    return newSessionId;
  }, []);

  // Effect that runs when we get an email from the chat
  useEffect(() => {
    console.log(
      "🔄 Email received:",
      currentContactRef.current?.properties?.email
    );
    if (
      currentContactRef.current?.properties?.email &&
      !hasLoadedHistoryRef.current
    ) {
      console.log(
        "Email received:",
        currentContactRef.current.properties.email
      );
      hasLoadedHistoryRef.current = true;
      loadChatHistory(
        sessionIdRef.current,
        currentContactRef.current.properties.email
      );
    }
  }, [currentContactRef.current?.properties?.email, loadChatHistory]);

  // Create initial session on mount
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // Load link mappings on mount
  useEffect(() => {
    loadLinkMappings();
  }, [loadLinkMappings]);

  // Add effect to handle URL email parameters
  useEffect(() => {
    const urlParams = getUrlParams();
    const urlEmail = urlParams.email;
    
    if (urlEmail && !hasProcessedUrlEmailRef.current) {
      console.log("🔗 Email found in URL:", urlEmail);
      hasProcessedUrlEmailRef.current = true;
      
      // Automatically set verification state to true when email is in URL
      const previousValue = isMachineSerialVerifiedRef.current;
      updateMachineSerialVerification(true);
      console.log("🔐 Auto-verification from URL email:", {
        from: previousValue,
        to: isMachineSerialVerifiedRef.current,
      });

      // Update session data with URL email
      const currentSession = getSessionData();
      const updatedSession = {
        ...currentSession,
        email: urlEmail,
        isMachineSerialVerified: true,
      };
      setSessionData(updatedSession);

      // Update email sessions mapping
      const emailSessions = getEmailSessions();
      emailSessions[urlEmail] = {
        sessionId: currentSession.sessionId,
        isMachineSerialVerified: true,
        timestamp: currentSession.timestamp,
      };
      setEmailSessions(emailSessions);

      console.log("📝 Updated session with URL email:", {
        email: urlEmail,
        session: updatedSession,
        emailSessions: getEmailSessions(),
      });

      // Fetch contact information for the URL email
      fetchContactFromEmail(urlEmail);
    }
  }, []);

  // Function to fetch contact information from email
  const fetchContactFromEmail = async (email: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.found && result.contacts) {
          const contact = JSON.parse(result.contacts)[0];
          currentContactRef.current = contact;
          console.log("👤 Contact loaded from URL email:", contact);
        }
      }
    } catch (error) {
      console.error("Error fetching contact from URL email:", error);
    }
  };

  // Add session expiry check effect
  useEffect(() => {
    const checkSessionExpiry = () => {
      const savedSession = getSessionData(); // Use our utility function
      if (Object.keys(savedSession).length > 0) {
        const isExpired = Date.now() - savedSession.timestamp > SESSION_TIMEOUT;

        if (isExpired) {
          console.log("🕒 Session expired:", {
            sessionAge:
              Math.round((Date.now() - savedSession.timestamp) / 1000) + "s",
            email: savedSession.email,
          });

          // If there's an email, clean up email sessions first
          if (savedSession.email) {
            const emailSessions = getEmailSessions(); // Use our utility function
            delete emailSessions[savedSession.email];
            setEmailSessions(emailSessions); // Use our utility function

            console.log("🗑️ Cleaned up email session mapping:", {
              email: savedSession.email,
              remainingSessions: Object.keys(emailSessions).length,
            });
          }

          // Clear chat session
          localStorage.removeItem("chatSession_v2");

          console.log("🔄 Session cleanup complete - triggering reload");
          setIsSessionExpired(true);
          window.location.reload();
        }
      }
    };

    // Check on mount
    checkSessionExpiry();

    // Set up interval to check periodically
    const interval = setInterval(checkSessionExpiry, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // Update the verification state setter
  const updateMachineSerialVerification = (verified: boolean) => {
    isMachineSerialVerifiedRef.current = verified;
    setIsMachineSerialVerified(verified);
  };

  return {
    messages: allMessages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    setMessages: setAllMessages,
    currentMessage,
    isStreaming,
    isMachineSerialVerified,
    currentContact: currentContactRef.current,
    isSessionExpired,
    waitingForReview,
    setWaitingForReview,
    sessionId: sessionIdRef.current,
    handleReviewSubmit,
  };
}
